/**
 * SPIN TENET — PALM Engine Adapter (Gate 2 Foundation)
 *
 * Implements the EngineAdapter boundary for PALM (Parallelized Atmospheric-Oceanic Model).
 */

import type { EngineDescriptor, RunSpecification, RunLifecycleStatus, CfdResult } from '@contracts/index';
import type { EngineAdapter, AdapterValidationResult, PreparedRunPayload, RunSubmissionResult } from './adapter';

export class PALMAdapter implements EngineAdapter {
  readonly descriptor: EngineDescriptor = {
    id: 'palm',
    displayName: 'PALM',
    version: '23.10 (Urban Microclimate)',
    availability: 'configured',
    capabilities: {
      supportedBoundaries: [
        'velocity_inlet',
        'pressure_outlet',
        'no_slip_wall',
        'slip_wall',
        'open'
      ],
      supportsSlipWalls: true,
      maxDomainDimensions: {
        maxWidth: 100000,
        maxLength: 100000,
        maxHeight: 10000
      },
      minBuildingCount: 1,
      description: 'Large-Eddy Simulation (LES) atmospheric model for urban microclimate, building thermal interaction, and boundary-layer flows.'
    },
    adapterId: 'adapter-palm-v1',
    description: 'Specialized atmospheric boundary layer LES model developed for complex urban microclimates.'
  };

  validate(specification: RunSpecification): AdapterValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // PALM atmospheric boundary layer requirement: minimum vertical extent
    if (specification.domain.height < 50.0) {
      errors.push(
        'ENGINE CANNOT REPRESENT THIS RUN SPECIFICATION: PALM atmospheric boundary layer model requires minimum domain height of 50.0 m.'
      );
    }

    const bc = specification.boundaryConditions;
    if (bc.buildings !== 'no_slip_wall') {
      errors.push('ENGINE CANNOT REPRESENT THIS RUN SPECIFICATION: PALM urban surface model requires building boundaries to be no_slip_wall.');
    }

    return {
      canRepresent: errors.length === 0,
      errors,
      warnings
    };
  }

  async prepare(specification: RunSpecification): Promise<PreparedRunPayload> {
    const val = this.validate(specification);
    if (!val.canRepresent) {
      throw new Error(`PALM cannot prepare run: ${val.errors.join('; ')}`);
    }
    return {
      caseId: `palm-case-${specification.runId}`,
      engine: 'palm',
      specification,
      preparedAt: new Date().toISOString()
    };
  }

  async submit(specification: RunSpecification): Promise<RunSubmissionResult> {
    const val = this.validate(specification);
    if (!val.canRepresent) {
      return {
        success: false,
        runId: specification.runId,
        engine: 'palm',
        state: 'FAILED',
        message: val.errors.join('; ')
      };
    }
    return {
      success: true,
      runId: specification.runId,
      engine: 'palm',
      state: 'QUEUED',
      message: 'Run specification accepted by PALM adapter and enqueued.'
    };
  }

  async getRunStatus(_runId: string): Promise<RunLifecycleStatus> {
    return 'QUEUED';
  }

  async getResult(_runId: string): Promise<CfdResult | null> {
    return null;
  }
}
